import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { TRACK_RELAY_ENABLED } from '../lib/featureFlags';
import { RouteContent, RouteLoading } from './RouteContent';
import type { EntryRoute } from './guards';
import { AuthPage } from '../pages/AuthPage';
import { RecordingPlaybackPage } from '../pages/RecordingPlaybackPage';
import { ChatGptActionPage } from '../pages/ChatGptActionPage';
import { ChatGptConnectPage } from '../pages/ChatGptConnectPage';
import { MobileBillingReturnPage } from '../pages/MobileBillingReturnPage';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { VerifyEmailPage } from '../pages/VerifyEmailPage';
import { PublicSessionDemoPage } from '../pages/demo/PublicSessionDemoPage';
import { KioskPage } from '../pages/kiosk/KioskPage';
import { KioskPairPage } from '../pages/kiosk/KioskPairPage';
import { SessionScanPage } from '../pages/SessionScanPage';
const LiveSharePage = TRACK_RELAY_ENABLED
  ? React.lazy(async () => {
      const module = await import('../pages/LiveSharePage');
      return { default: module.LiveSharePage };
    })
  : null;

function EntryContent({
  entry,
  kioskSessionNotice,
}: {
  entry: EntryRoute;
  kioskSessionNotice: React.ReactNode;
}) {
  switch (entry) {
    case 'mobile-billing-return':
      return (
        <Routes>
          <Route path="/mobile/billing-return" element={<MobileBillingReturnPage />} />
          <Route path="*" element={<Navigate to="/mobile/billing-return" replace />} />
        </Routes>
      );
    case 'demo':
      return (
        <Routes>
          <Route path="/demo/session" element={<PublicSessionDemoPage />} />
          <Route path="*" element={<Navigate to="/demo/session" replace />} />
        </Routes>
      );
    case 'live-share':
      return LiveSharePage ? (
        <Routes>
          <Route path="/live/:token" element={<LiveSharePage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      ) : (
        <Navigate to="/login" replace />
      );
    case 'kiosk':
      return (
        <Routes>
          <Route path="/kiosk" element={<KioskPage />} />
        </Routes>
      );
    case 'loading':
      return <RouteLoading fullPage />;
    case 'return-kiosk':
      return (
        <Routes>
          <Route path="*" element={<Navigate to="/kiosk" replace />} />
        </Routes>
      );
    case 'password-recovery':
      return (
        <Routes>
          <Route path="/login" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/login?view=forgot-password" replace />} />
        </Routes>
      );
    case 'recording':
      return <RecordingPlaybackPage />;
    case 'chatgpt-action':
      return <ChatGptActionPage />;
    case 'chatgpt-connect':
      return <ChatGptConnectPage />;
    case 'signed-out':
      return (
        <Routes>
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/k/:token" element={<AuthPage />} />
          <Route path="/s/v1/:locator" element={<SessionScanPage />} />
          <Route path="/s/v1/*" element={<SessionScanPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      );
    case 'kiosk-pair':
      return (
        <Routes>
          <Route path="/k/:token" element={<KioskPairPage />} />
        </Routes>
      );
    case 'session-scan':
      return (
        <>
          {kioskSessionNotice}
          <Routes>
            <Route path="/s/v1/:locator" element={<SessionScanPage />} />
            <Route path="/s/v1/*" element={<SessionScanPage />} />
          </Routes>
        </>
      );
    case 'setup-registration':
      return (
        <Routes>
          <Route path="/register" element={<AuthPage />} />
          <Route path="*" element={<Navigate to="/register" replace />} />
        </Routes>
      );
  }
}
export function EntryRoutes(props: { entry: EntryRoute; kioskSessionNotice: React.ReactNode }) {
  return (
    <RouteContent fallbackPath="/login">
      <EntryContent {...props} />
    </RouteContent>
  );
}
